import { Component, Input, OnChanges, SecurityContext, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ViewMode } from '../../../../models';
import {
  CheckoutExtensionUI,
  CheckoutUpsellExtension,
  ProductReviews,
  UpsellFreeShippingThresholdReplaceValues,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { IUpsellThresholdTypes } from '@pdqdev/pdq-shared/dist/interfaces/IThresholdTypes';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  TIMER_STATES,
  TIME_FORMATS,
  APPEARANCE_TYPES,
  EXTENSION_TIMER_CONFIGS,
} from '@pdqdev/pdq-shared/dist/utils/extensions-timer-utils';
import {
  getTimerPreviewMode,
  getExampleCountdown,
  isMessageEmpty,
  formatMessageWithCountdown,
  getTimerMessageText,
  getTimerBarAlignment,
  getTimerStyleClass,
  getTimerBarBannerIcon,
} from '@utils/timer-utils';
import {
  ExtensionToCssBorderRadius,
  ExtensionToCssBorderType,
  ExtensionToCssBorderWidth,
  ExtensionToCssPadding,
} from '../../../extension-components/checkout-content/checkout-content.utils';
import {
  AutonomousDiscountConfiguration,
  DiscountMode,
  resolveAutonomousDiscountLabel,
} from '../../../extension-components/checkout-upsell/autonomous-discount';
import UpsellTextType = CheckoutUpsellExtension.UpsellTextType;
import GoalsDisplayMode = CheckoutUpsellExtension.GoalsDisplayMode;

interface SingleBarCheckpoint {
  iconUrl: string;
  thresholdLabel: string;
  progressBarLabel: string;
  showInfo: boolean;
  originalIndex: number;
}

interface InlineBarGoal {
  fillPercent: number;
  iconUrl: string;
  progressBarLabel: string;
  showInfo: boolean;
  originalIndex: number;
}

const INLINE_BAR_ACTIVE_GOAL_FILL_PERCENT = 70;
const NUMBER_OF_PREVIEW_CARDS_ARROWS = 2;
// Scroll mode renders more cards than fit so the preview shows the strip actually scrolling
const NUMBER_OF_PREVIEW_CARDS_SCROLL = 4;
const NUMBER_OF_PREVIEW_CARDS_MOBILE = 2;

const PREVIEW_BORDER_COLOR = 'var(--shopify-control-border)';
const DISCOUNT_BADGE_FALLBACK_TEXT = '25%';
const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', EUR: '\u20ac', GBP: '\u00a3', CAD: 'CA$', AUD: 'A$' };

const currencySymbolFor = (currency?: string): string => CURRENCY_SYMBOLS[currency] || '$';

const TextSizeToCss = new Map<string, string>([
  ['extraSmall', '10px'],
  ['small', '12px'],
  ['base', '14px'],
  ['medium', '16px'],
  ['large', '18px'],
  ['extraLarge', '21px'],
]);

@Component({
  selector: 'checkout-upsell-example',
  templateUrl: './checkout-upsell-example.component.html',
  styleUrls: ['./checkout-upsell-example.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None, //this is to make the header text bigger and not be overwritten by browser style on h1
})
export class CheckoutUpsellExampleComponent implements OnChanges {
  @Input() template;
  @Input() mode: ViewMode = null;
  @Input() previewMode: 'threshold' | 'upsell' = 'threshold';
  @Input() isMobile: boolean = false;
  @Input() previewedGoalIndex: number = 0;
  @Input() previewedTextType: UpsellTextType = UpsellTextType.Threshold;
  upsellThresholdType = IUpsellThresholdTypes;
  UpsellFreeShippingThresholdReplaceValues = UpsellFreeShippingThresholdReplaceValues;
  UpsellTextType = UpsellTextType;
  upsellRowIndices: number[] = [];
  upsellCardIndices: number[] = [1, NUMBER_OF_PREVIEW_CARDS_ARROWS];
  isScrollNavigation = false;
  mainBackgroundStyle: Record<string, string> = {};
  cardBackgroundStyle: Record<string, string> = {};
  imageBackgroundStyle: Record<string, string> = {};
  isOriginalImageFit = false;
  ctaButtonText = '';
  showVariantDropdown = false;
  showDiscountBadge = false;
  discountBadgeStyle: Record<string, string> = {};
  discountBadgeText = '';
  showDiscountLabel = false;
  discountLabelText = '';
  discountLabelTone = '';

  // Sample values so the preview shows a badge without calling the reviews API.
  readonly previewReviewRating = 4.5;
  readonly previewReviewCount = 128;
  readonly ReviewsPosition = ProductReviews.Position;

  constructor(private sanitizer: DomSanitizer) {}

  get reviewsConfig(): CheckoutUpsellExtension.ProductReviewsConfig | null {
    const config = this.template?.settings?.productReviews;
    return config?.enabled ? config : null;
  }

  get previewReviewCountOrNull(): number | null {
    return this.reviewsConfig?.showNumberOfReviews ? this.previewReviewCount : null;
  }

  get previewReviewRatingOrZero(): number {
    return this.reviewsConfig?.showStars ? this.previewReviewRating : 0;
  }

  showsReviewsAt(position: ProductReviews.Position): boolean {
    return this.reviewsConfig?.position === position;
  }
  get isSequentialMode(): boolean {
    return this.template?.goalsConfiguration?.displayMode === GoalsDisplayMode.SEQUENTIAL;
  }

  get isInlineMode(): boolean {
    return this.template?.goalsConfiguration?.displayMode === GoalsDisplayMode.INLINE;
  }

  get inlineBarGoals(): InlineBarGoal[] {
    const goals = this.template?.goalsConfiguration?.goals;
    if (!goals?.length) return [];

    const enabledGoals: InlineBarGoal[] = [];
    goals.forEach((goal, index) => {
      if (goal.enabled === false) return;
      enabledGoals.push({
        fillPercent: 0,
        iconUrl: goal.iconUrl || '',
        progressBarLabel: goal.progressBarLabel || '',
        showInfo: !!goal.showInfoInProgressBar,
        originalIndex: index,
      });
    });

    const goalIndex = this.effectiveGoalIndex;
    const activeIdx = enabledGoals.findIndex((g) => g.originalIndex === goalIndex);

    enabledGoals.forEach((g, i) => {
      if (activeIdx === -1) return;
      if (i < activeIdx) {
        g.fillPercent = 100;
      } else if (i === activeIdx) {
        g.fillPercent =
          this.previewedTextType === UpsellTextType.NoThreshold && this.mode
            ? 100
            : INLINE_BAR_ACTIVE_GOAL_FILL_PERCENT;
      }
    });

    return enabledGoals;
  }

  get sequentialSectionStyle(): Record<string, string> {
    const gc = this.template?.goalsConfiguration;
    if (!gc) return {};
    return {
      padding: ExtensionToCssPadding.get(gc.padding) || '14px',
      'border-radius': ExtensionToCssBorderRadius.get(gc.cornerRadius) || '5px',
      'border-width': ExtensionToCssBorderWidth.get(gc.borderWidth) || '1px',
      'border-style': ExtensionToCssBorderType.get(gc.border) || 'solid',
      'border-color': '#dedede',
    };
  }

  get sequentialTextStyle(): Record<string, string> {
    const textSize = this.template?.goalsConfiguration?.textSize;
    return {
      'font-size': TextSizeToCss.get(textSize) || '14px',
    };
  }

  private get firstEnabledGoalIndex(): number {
    const goals = this.template?.goalsConfiguration?.goals;
    if (!goals?.length) return 0;
    const idx = goals.findIndex((g) => g.enabled !== false);
    return idx >= 0 ? idx : 0;
  }

  private get effectiveGoalIndex(): number {
    return !this.mode ? this.firstEnabledGoalIndex : this.previewedGoalIndex;
  }

  get previewedGoalIconUrl(): string {
    const goals = this.template?.goalsConfiguration?.goals;
    if (!goals?.length) return '';
    const goal = goals[this.effectiveGoalIndex];
    return goal?.iconUrl || '';
  }

  get previousGoalPills(): { label: string }[] {
    if (!this.template?.goalsConfiguration?.showPreviousGoals) return [];
    const goals = this.template?.goalsConfiguration?.goals;
    if (!goals?.length || this.previewedGoalIndex < 1) return [];

    const pills: { label: string }[] = [];
    for (let i = 0; i < this.previewedGoalIndex; i++) {
      const goal = goals[i];
      if (goal?.enabled !== false) {
        pills.push({ label: goal.progressBarLabel || `Goal ${i + 1}` });
      }
    }
    return pills;
  }

  get sequentialProgressBarColor(): string {
    return this.template?.goalsConfiguration?.progressBarColor || '#4A7D4A';
  }

  get singleBarCheckpoints(): SingleBarCheckpoint[] {
    const goals = this.template?.goalsConfiguration?.goals;
    if (!goals?.length) return [];

    const checkpoints: SingleBarCheckpoint[] = [];
    goals.forEach((goal, index) => {
      if (goal.enabled === false) return;

      let thresholdLabel = '';
      if (goal.showInfoInProgressBar) {
        if (index === 0) {
          const freeThreshold = this.template?.configure?.freeThresholds?.[0];
          if (freeThreshold?.threshold != null) {
            thresholdLabel = this.formatThresholdValue(freeThreshold.threshold, freeThreshold.currency);
          }
        } else {
          const goalThreshold = goal.thresholds?.[0];
          if (goalThreshold?.threshold != null) {
            thresholdLabel = this.formatThresholdValue(goalThreshold.threshold, goalThreshold.currency);
          }
        }
      }

      checkpoints.push({
        iconUrl: goal.iconUrl || '',
        thresholdLabel,
        progressBarLabel: goal.progressBarLabel || '',
        showInfo: !!goal.showInfoInProgressBar,
        originalIndex: index,
      });
    });

    return checkpoints;
  }

  get singleBarFillPercentage(): number {
    const checkpoints = this.singleBarCheckpoints;
    const n = checkpoints.length;
    if (n === 0) return 0;

    const goalIndex = this.effectiveGoalIndex;
    const checkpointIndex = checkpoints.findIndex((cp) => cp.originalIndex === goalIndex);
    if (checkpointIndex === -1) return 0;

    const position = ((checkpointIndex + 1) / n) * 100;

    if (this.previewedTextType === UpsellTextType.NoThreshold && this.mode) {
      return position;
    }
    return Math.max(0, position - 10);
  }

  get singleBarProgressColor(): string {
    return this.template?.goalsConfiguration?.progressBarColor || '#4A7D4A';
  }

  get progressBarLabelSizeStyle(): Record<string, string> {
    const size = this.template?.goalsConfiguration?.progressBarLabelSize;
    return { 'font-size': TextSizeToCss.get(size) || TextSizeToCss.get(CheckoutExtensionUI.TextSize.EXTRA_SMALL) };
  }

  isCheckpointCompleted(checkpointIndex: number): boolean {
    const checkpoints = this.singleBarCheckpoints;
    if (checkpoints.length === 0) return false;
    const goalIndex = this.effectiveGoalIndex;
    const activeCheckpointIndex = checkpoints.findIndex((cp) => cp.originalIndex === goalIndex);
    if (activeCheckpointIndex === -1) return false;
    if (this.previewedTextType === UpsellTextType.NoThreshold && this.mode) {
      return checkpointIndex <= activeCheckpointIndex;
    }
    return checkpointIndex < activeCheckpointIndex;
  }

  get shouldShowIconInSequential(): boolean {
    const showThresholdText =
      (this.previewMode === 'threshold' || !this.mode) &&
      this.template?.settings?.thresholdType !== this.upsellThresholdType.NoThreshold;
    return showThresholdText;
  }

  // Arrow navigation pages through a fixed number of cards, scroll navigation renders a longer strip
  private get numberOfCardsToPreview(): number {
    if (this.isScrollNavigation) return NUMBER_OF_PREVIEW_CARDS_SCROLL;
    return this.isMobile ? NUMBER_OF_PREVIEW_CARDS_MOBILE : NUMBER_OF_PREVIEW_CARDS_ARROWS;
  }

  // The preview shows the display type the merchant picked, on both surfaces — two cards fit a phone
  get effectiveDisplayType(): string {
    const displayType = this.template?.style?.displayType;
    if (displayType === CheckoutUpsellExtension.DisplayType.HIDE_PRODUCTS) return 'hide-products';

    if (displayType === CheckoutUpsellExtension.DisplayType.ROW) return 'row';
    if (displayType === CheckoutUpsellExtension.DisplayType.COMPACT) return 'compact';
    return 'col';
  }

  // The extension still swaps cards for the merchant's mobile choice on a real phone, so flag when
  // what ships will differ from what this preview shows
  get shouldShowMobileOverrideMessage(): boolean {
    return (
      this.isMobile &&
      this.template?.style?.displayType === CheckoutUpsellExtension.DisplayType.CARDS &&
      this.template?.style?.mobileDisplayType === CheckoutUpsellExtension.DisplayType.ROW
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.template) {
      const numberOfRows = this.template?.style?.numberOfRowsInRowDisplayType || 1;
      this.upsellRowIndices = Array.from({ length: numberOfRows }, (_, i) => i);
    }
    if (changes.template || changes.isMobile) {
      this.isScrollNavigation =
        this.template?.style?.carouselNavigationStyle === CheckoutUpsellExtension.CarouselNavigationStyle.SCROLL;
      this.upsellCardIndices = Array.from({ length: this.numberOfCardsToPreview }, (_, i) => i + 1);
    }
    if (changes.template) {
      this.applyAppearance();
    }
  }
  get previewDisplayText(): string {
    if (this.template?.settings?.thresholdType === this.upsellThresholdType.NoThreshold) {
      return this.template?.settings?.noThresholdDisplayTitle || '';
    }

    const goals = this.template?.goalsConfiguration?.goals;
    const showThresholdText =
      (this.previewMode === 'threshold' || !this.mode) &&
      this.template?.settings?.thresholdType !== this.upsellThresholdType.NoThreshold;

    let text = '';
    const goalIndex = this.effectiveGoalIndex;

    if (!this.mode && goals?.length > 0) {
      text =
        goalIndex === 0 ? this.template?.settings?.textWithThreshold || '' : goals[goalIndex]?.textWithThreshold || '';
    } else if (goalIndex === 0) {
      text = showThresholdText
        ? this.template?.settings?.textWithThreshold || ''
        : this.template?.settings?.textWithoutThreshold || '';
    } else if (goals?.length > 0 && goals[goalIndex]) {
      const goal = goals[goalIndex];
      text = showThresholdText ? goal.textWithThreshold || '' : goal.textWithoutThreshold || '';
    }

    if (text) {
      if (this.template?.settings?.thresholdType === this.upsellThresholdType.CartValue) {
        text = text.replace(UpsellFreeShippingThresholdReplaceValues.AmountLeft, '$ 5.99');
      } else {
        text = text.replace(UpsellFreeShippingThresholdReplaceValues.ItemsLeft, '2');
      }
    }

    return text;
  }

  safeHtmlTextPrice(): SafeHtml {
    const richText = this.template?.settings?.sellingPlanDescription;
    if (!richText) {
      return '';
    }
    const texts = richText.replace('{price}', '$9.99');
    const sanitizedText = this.sanitizer.sanitize(SecurityContext.HTML, texts);
    return sanitizedText;
  }

  shouldShowTimer(): boolean {
    const timerConfig = this.template?.timerConfiguration;
    if (!timerConfig?.enableCountdownTimer) return false;

    const thresholdType = this.template?.settings?.thresholdType;
    if (thresholdType === this.upsellThresholdType.NoThreshold) {
      return true;
    }

    if (!this.mode) {
      return true;
    }

    return this.previewMode === 'threshold';
  }

  private getTimerPreviewMode(): string {
    return getTimerPreviewMode(this.template?.timerConfiguration, TIMER_STATES.DEFAULT);
  }

  private getExampleCountdown(): string {
    return getExampleCountdown(this.template?.timerConfiguration, TIME_FORMATS.DIGITAL);
  }

  isTimerMessageEmpty(): boolean {
    const previewMode = this.getTimerPreviewMode();
    const timerConfig = this.template?.timerConfiguration;

    if (previewMode === TIMER_STATES.EXPIRED) {
      return isMessageEmpty(timerConfig?.expiredMessage);
    } else if (previewMode === TIMER_STATES.LOW) {
      return isMessageEmpty(timerConfig?.lowTimerMessage);
    } else {
      return isMessageEmpty(timerConfig?.defaultMessage);
    }
  }

  getTimerMessageText(): string {
    const previewMode = this.getTimerPreviewMode();
    const countdown = this.getExampleCountdown();
    return getTimerMessageText(this.template?.timerConfiguration, previewMode, countdown, TIMER_STATES);
  }

  getTimerBarText(): string {
    return this.getTimerMessageText();
  }

  getGoalMessageWithCountdown(message: string): string {
    if (!message || !message.includes('{countdown}')) {
      return message;
    }
    const countdown = this.getExampleCountdown();
    return formatMessageWithCountdown(message, countdown);
  }

  getTimerBarAlignment(): string {
    const previewMode = this.getTimerPreviewMode();
    const timerConfig = this.template?.timerConfiguration;

    let message = '';
    if (previewMode === TIMER_STATES.EXPIRED) {
      message = timerConfig?.expiredMessage || '';
    } else if (previewMode === TIMER_STATES.LOW) {
      message = timerConfig?.lowTimerMessage || '';
    } else {
      message = timerConfig?.defaultMessage || '';
    }

    return getTimerBarAlignment(message);
  }

  getTimerBarClass(): string {
    const previewMode = this.getTimerPreviewMode();
    return getTimerStyleClass(
      this.template?.timerConfiguration,
      previewMode,
      'timer-bar',
      TIMER_STATES,
      APPEARANCE_TYPES,
    );
  }

  getTimerBadgeText(): string {
    return this.getTimerMessageText();
  }

  getTimerBadgeClass(): string {
    const previewMode = this.getTimerPreviewMode();
    return getTimerStyleClass(
      this.template?.timerConfiguration,
      previewMode,
      'timer-badge',
      TIMER_STATES,
      APPEARANCE_TYPES,
    );
  }

  getTimerBarBannerIcon(): string {
    const previewMode = this.getTimerPreviewMode();
    return getTimerBarBannerIcon(this.template?.timerConfiguration, previewMode, TIMER_STATES, APPEARANCE_TYPES);
  }

  readonly TIMER_PLACEMENTS = EXTENSION_TIMER_CONFIGS.UPSELL.TIMER_PLACEMENTS;

  private applyAppearance(): void {
    const style = this.template?.style;
    const settings = this.template?.settings;

    this.mainBackgroundStyle = this.buildBoxStyle(style?.mainBackgroundStyling);
    this.cardBackgroundStyle = this.buildBoxStyle(style?.cardBackgroundStyling);
    // extension only pads the image when it has a border to sit against
    const imageHasBorder = this.hasBorder(style?.imageStyling?.border);
    this.imageBackgroundStyle = this.buildBoxStyle(style?.imageStyling);
    if (!imageHasBorder) {
      this.imageBackgroundStyle.padding = '0';
    }
    this.isOriginalImageFit = style?.imageStyling?.imageFit === 'original';

    this.showVariantDropdown = settings?.variantSelector === 'inside';
    // popup selector: the cta carries the variant wording instead
    this.ctaButtonText =
      settings?.variantSelector === 'outside' && settings?.variantButtonText
        ? settings.variantButtonText
        : settings?.textAddToOrder || '';

    this.applyDiscountAppearance();
  }

  private hasBorder(border?: string): boolean {
    return !!border && border !== CheckoutExtensionUI.BorderType.NONE;
  }

  // 'none' collapses to 0 like the live extension does — image runs card edge to edge
  private buildBoxStyle(styling?: CheckoutUpsellExtension.BackgroundStyling): Record<string, string> {
    if (!styling) return {};

    const boxStyle: Record<string, string> = {
      padding: ExtensionToCssPadding.get(styling.padding) || '0',
      'border-radius': ExtensionToCssBorderRadius.get(styling.cornerRadius) || '0',
    };
    const borderStyle = ExtensionToCssBorderType.get(styling.border);
    if (borderStyle && borderStyle !== 'none') {
      boxStyle['border-style'] = borderStyle;
      boxStyle['border-width'] = ExtensionToCssBorderWidth.get(styling.borderWidth) || '1px';
      boxStyle['border-color'] = PREVIEW_BORDER_COLOR;
    }
    return boxStyle;
  }

  private applyDiscountAppearance(): void {
    const settings = this.template?.settings;
    const discountEnabled = !!settings?.showDiscountBadgeOrText;

    this.showDiscountBadge = discountEnabled && settings?.discountBadgeDisplayType !== 'text';
    this.showDiscountLabel = discountEnabled && settings?.discountBadgeDisplayType === 'text';

    this.discountBadgeStyle = this.showDiscountBadge
      ? {
          'background-color': settings?.discountBadgeBackgroundColor,
          color: settings?.discountBadgeTextColor,
          'border-radius': ExtensionToCssBorderRadius.get(settings?.discountBadgeCornerRadius) || '0',
        }
      : {};
    this.discountBadgeText = this.showDiscountBadge ? this.resolveDiscountText() : '';
    this.discountLabelText = this.resolveDiscountLabelText();
    this.discountLabelTone = this.template?.discountConfiguration?.labelColor || 'default';
  }

  private resolveDiscountLabelText(): string {
    const configuration: AutonomousDiscountConfiguration = this.template?.discountConfiguration;
    if (configuration?.mode !== DiscountMode.Autonomous) {
      return configuration?.labelText || '';
    }
    return resolveAutonomousDiscountLabel(configuration, configuration.discountValue);
  }

  private resolveDiscountText(): string {
    const discount = this.firstConfiguredDiscount();
    if (!discount?.discountValue) {
      return DISCOUNT_BADGE_FALLBACK_TEXT;
    }
    if (discount.discountType === CheckoutUpsellExtension.DiscountType.Percentage) {
      return `-${discount.discountValue}%`;
    }
    const currency = this.template?.configure?.freeThresholds?.[0]?.currency;
    return `-${currencySymbolFor(currency)}${discount.discountValue}`;
  }

  // per-group discounts win over the legacy extension-level one
  private firstConfiguredDiscount(): CheckoutUpsellExtension.DiscountConfiguration {
    const ruleGroups: CheckoutUpsellExtension.ProductSelection[] = this.template?.productSelection ?? [];
    const groupDiscount = ruleGroups
      .flatMap((ruleGroup) => ruleGroup?.discounts ?? [])
      .find((discount) => discount?.discountValue);
    return groupDiscount ?? this.template?.discountConfiguration;
  }

  private formatThresholdValue(threshold: number, currency?: string): string {
    if (this.template?.settings?.thresholdType === IUpsellThresholdTypes.CartValue) {
      const formattedValue = threshold % 1 === 0 ? threshold.toFixed(0) : threshold.toFixed(2);
      return `${currencySymbolFor(currency)}${formattedValue}`;
    }
    return `${threshold} items`;
  }
}
